<template>
  <validation-observer ref="form" novalidate>
    <div class="documents-tab">
      <div class="document-summary">
        <div
          v-for="item in counts"
          :key="item.status.id"
          class="document-summary__item"
        >
          <div class="document-summary__count">
            {{ item.count | padDocumentCount }}
          </div>
          <custom-balloon
            :background-color="item.status.color"
            dot
            :text="item.status.name"
          />
        </div>
      </div>

      <section-title class="my-3" content="Documentos Propuesta" />

      <div v-if="items.length" class="document-list">
        <result-row
          class="my-2"
          v-for="item in items"
          :key="item.typeId"
          :history="historyByDocumentId[item.id] || []"
          :is-history-loading="Boolean(loadingHistoryByDocumentId[item.id])"
          :is-open="activeHistoryDocumentId === item.id"
          :is-read-only="isReadonlyMode"
          :item="item"
          @close-history="closeHistory"
          @download="download"
          @history="loadHistory"
          @remove="remove"
          @replace="replace"
          @select="selectType"
        />
      </div>

      <custom-result-not-found
        v-else
        class="mt-4 row pt-3"
        text="No hay documentos disponibles."
      />

      <div v-if="canShowDocumentForm" class="document-form">
        <div class="document-form__type">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="model.type"
              custom-error-msg="Favor seleccionar el tipo de documento."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              label="Tipo de Documento:"
              :options="documentFormDocumentTypes"
              placeholder="Seleccione"
            />
          </validation-provider>
        </div>
        <div class="document-form__file">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-file-input
              id="referralDocumentFile"
              :key="fileInputKey"
              v-model="model.file"
              :accept="'.pdf'"
              custom-error-msg="Favor seleccionar un documento PDF."
              display-style="normal"
              :error="errors[0]"
              label="Archivo:"
              label-position="left"
              :on-download="downloadSelectedFile"
            />
          </validation-provider>
        </div>
        <div class="document-form__actions">
          <action-button
            v-if="isReplacingDocument"
            i="fas fa-times-circle"
            text="Cancelar"
            variant="outline-danger"
            @click="clearForm"
          />
          <action-button
            i="fas fa-file-upload"
            text="Añadir"
            variant="success"
            @click="add"
          />
        </div>
      </div>
    </div>
  </validation-observer>
</template>

<script>
import alertMixin from '@/mixin/alert.mixin.js';
import objectToFormData from 'object-to-formdata';

import ActionButton from 'ActionButton';
import CustomBalloon from 'custom-balloon';
import CustomDropDown from 'custom-drop-down';
import CustomFileInput from 'CustomFileInput';
import CustomResultNotFound from 'custom-result-not-found';
import SectionTitle from 'SectionTitle';

import documentsService from '@/services/documents';
import {
  getDocumentHistory,
  getDocuments,
  getSaee12Report,
  getSaee12bReport,
  removeDocument,
  saveDocument,
} from '@/services/vocational-rehabilitation-referral/api.js';
import DocumentRequirementTypes from '@/utils/constants/vocational-rehabilitation-referral-document-requirement-types';

import ResultRow from './components/ResultRow.vue';

export default {
  name: 'DocumentsTab',
  components: {
    ActionButton,
    CustomBalloon,
    CustomDropDown,
    CustomFileInput,
    CustomResultNotFound,
    ResultRow,
    SectionTitle,
  },
  filters: {
    padDocumentCount(value) {
      return String(value || 0).padStart(2, '0');
    },
  },
  mixins: [alertMixin],
  props: {
    id: {
      type: [Number, String],
      default: () => null,
    },
    value: {
      type: Object,
      required: true,
    },
    readOnly: Boolean,
    isReadOnly: Boolean,
    validationItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      counts: [],
      documentTypes: [],
      historyByDocumentId: {},
      items: [],
      loadingHistoryByDocumentId: {},
      activeHistoryDocumentId: null,
      replacementItem: null,
      fileInputKey: 0,
    };
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    isReadonlyMode() {
      return this.readOnly || this.isReadOnly;
    },
    availableDocumentTypes() {
      return this.documentTypes;
    },
    canAddDocuments() {
      return !this.isReadonlyMode && this.availableDocumentTypes.length > 0;
    },
    canShowDocumentForm() {
      return this.canAddDocuments || this.isReplacingDocument;
    },
    documentFormDocumentTypes() {
      if (!this.isReplacingDocument) return this.availableDocumentTypes;

      return this.model.type ? [this.model.type] : [];
    },
    isReplacingDocument() {
      return !this.isReadonlyMode && Boolean(this.replacementItem);
    },
  },
  async mounted() {
    await this.get();
  },
  methods: {
    async get() {
      const { data } = await getDocuments(this.id);
      this.documentTypes = data?.documentTypes || [];
      this.items = data?.items || [];
      this.counts = data?.counts || [];
    },
    selectType(item) {
      this.replacementItem = null;
      this.model = {
        ...this.model,
        type: this.documentTypes.find((type) => type.id === item.typeId),
      };
    },
    replace(item) {
      if (!item.canReplace) return;

      this.replacementItem = item;
      this.model = {
        type: {
          id: item.typeId,
          name: item.typeName,
        },
        file: null,
      };
      this.$nextTick(() => this.$refs.form?.reset());
    },
    async add() {
      const valid = await this.$refs.form.validate();
      if (!valid) return;

      try {
        const selectedItem =
          this.replacementItem ||
          this.items.find((item) => item.typeId === this.model.type?.id);
        const hasAvailableType = this.documentTypes.some(
          (type) => type.id === this.model.type?.id
        );

        if (!this.isReplacingDocument && !hasAvailableType) {
          this.ShowToast(
            'Error',
            'El tipo de documento seleccionado no está disponible para este referido.',
            'error'
          );
          return;
        }

        if (selectedItem?.id && !selectedItem.canReplace) {
          this.ShowToast(
            'Error',
            'Este documento no puede ser reemplazado en su estado actual.',
            'error'
          );
          return;
        }
        const payload = {
          id: selectedItem?.id || null,
          typeId: this.model.type.id,
          file: this.model.file,
        };
        await saveDocument(
          this.id,
          objectToFormData(payload, { indices: true })
        );
        await this.get();
        this.clearForm();
        this.$emit('clear-validation');
        this.ShowToast(
          'Éxito',
          'El documento fue guardado correctamente.',
          'success'
        );
      } catch {
        this.ShowToast(
          'Error',
          'No se pudo guardar el documento. Intente nuevamente.',
          'error'
        );
      }
    },
    clearForm() {
      this.replacementItem = null;
      this.model = {
        type: null,
        file: null,
      };
      this.fileInputKey += 1;
      this.$nextTick(() => this.$refs.form?.reset());
    },
    async download(item) {
      if (item.reportRoute === 'saee-12' && item.reportId) {
        const { data } = await getSaee12Report(item.reportId);
        documentsService.Download(data);
        return;
      }

      if (item.reportRoute === 'saee-12b' && item.reportParameters) {
        const { data } = await getSaee12bReport(item.reportParameters);
        documentsService.Download(data);
        return;
      }

      if (!item.documentId) return;
      await documentsService.downloadFromServer(item.documentId);
    },
    async loadHistory(item) {
      if (!item?.id || this.loadingHistoryByDocumentId[item.id]) return;

      this.activeHistoryDocumentId = item.id;
      this.$set(this.loadingHistoryByDocumentId, item.id, true);

      try {
        const { data } = await getDocumentHistory(this.id, item.id);
        this.$set(this.historyByDocumentId, item.id, data || []);
      } catch {
        this.ShowToast(
          'Error',
          'No se pudo cargar el historial del documento. Intente nuevamente.',
          'error'
        );
      } finally {
        this.$set(this.loadingHistoryByDocumentId, item.id, false);
      }
    },
    closeHistory() {
      this.activeHistoryDocumentId = null;
    },
    downloadSelectedFile() {
      return Promise.resolve();
    },
    async remove(item) {
      const { value: confirmed, dismiss } = await this.ShowCancelModalClosable(
        '¿Eliminar documento?',
        'Este documento se eliminará del referido.'
      );
      const cancelled = ['close', 'backdrop'].includes(dismiss);
      if (cancelled || !confirmed) return;

      try {
        await removeDocument(this.id, item.id);
        await this.get();
        this.ShowToast(
          'Éxito',
          'El documento fue eliminado correctamente.',
          'success'
        );
      } catch {
        this.ShowToast(
          'Error',
          'No se pudo eliminar el documento. Intente nuevamente.',
          'error'
        );
      }
    },
    validate() {
      const nonRequiredTypeIds = [
        DocumentRequirementTypes.StateMeasurementReport,
        DocumentRequirementTypes.GradeReport,
        DocumentRequirementTypes.VocationalInterestInventory,
      ];
      const missingDocuments = (this.documentTypes || []).filter(
        (type) => !nonRequiredTypeIds.includes(type.id)
      );
      const hasAllRequiredDocuments = missingDocuments.length === 0;

      return Promise.resolve({
        tabId: 'Documents',
        title: 'Documentos',
        valid: hasAllRequiredDocuments,
        items: hasAllRequiredDocuments
          ? []
          : missingDocuments.map((type) => ({
              id: `document-${type.id}`,
              label: type.name,
              selector: '.documents-tab',
            })),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.documents-tab {
  padding: 18px 8px 8px;
}

.tab-validation-warning {
  background: #fff7e8;
  border: 1px solid #d99025;
  border-radius: 4px;
  color: #8a5200;
  padding: 10px 12px;
}

.tab-validation-warning__message {
  align-items: center;
  display: flex;
  font-weight: 700;
  margin-bottom: 6px;
}

.tab-validation-warning__items {
  margin: 0;
  padding-left: 26px;
}

.tab-validation-warning__items li {
  margin: 2px 0;
}

.document-summary {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(4, minmax(130px, 1fr));
}

.document-summary__item {
  border: 1px solid #e2e7e4;
  border-radius: 4px;
  min-height: 82px;
  padding: 14px 16px;
}

.document-summary__count {
  color: #686f70;
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 12px;
}

.document-list {
  display: grid;
  gap: 8px;
}

.document-form {
  align-items: end;
  background: #f8f9f8;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(220px, 0.8fr) minmax(320px, 1.35fr) max-content;
  margin-top: 14px;
  padding: 16px;
}

.document-form__actions {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

::v-deep .custom-input-label,
::v-deep .input-label,
::v-deep .data-label {
  color: #111 !important;
  font-size: 13px;
  font-weight: 500;
}

@media (max-width: 991.98px) {
  .document-summary,
  .document-form {
    grid-template-columns: 1fr;
  }

  .document-form__actions {
    justify-content: flex-start;
  }
}
</style>
